import React, { useState, useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Link2, LinkIcon, Heading1, Heading2, Heading3,
  Image as ImageIcon, Undo2, Redo2, Send, Clock, X, Plus, ChevronDown, Loader2,
  Code, Quote, Minus, Type, Palette, RotateCcw, Variable
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useTranslation } from "react-i18next";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "./ui/sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Variables available for templates
const VARIABLES = [
  { key: "nome_contato", label: "Nome do Contato" },
  { key: "email_contato", label: "E-mail do Contato" },
  { key: "nome_empresa", label: "Nome da Empresa" },
  { key: "titulo_negocio", label: "Título do Negócio" },
  { key: "nome_responsavel", label: "Responsável" },
  { key: "etapa_atual", label: "Etapa Atual" },
  { key: "pipeline", label: "Pipeline" },
  { key: "valor_negocio", label: "Valor do Negócio" },
];

function resolveVariable(key, negocio, contatos, empresas, stages, pipeline) {
  const contato = contatos?.[0];
  const empresa = empresas?.[0];
  const stage = stages?.find(s => s.id === negocio?.estagio_id);
  switch (key) {
    case "nome_contato": return contato?.nome || negocio?.contato_nome || "";
    case "email_contato": {
      const emails = (contato?.tipos_contato || []).filter(tc => ["e-mail", "email"].includes(tc.tipo?.toLowerCase()));
      return emails[0]?.valor || contato?.custom_fields?.email || "";
    }
    case "nome_empresa": return empresa?.nome || negocio?.empresa_nome || "";
    case "titulo_negocio": return negocio?.titulo || "";
    case "nome_responsavel": return negocio?.responsavel_nome || "";
    case "etapa_atual": return stage?.name || "";
    case "pipeline": return pipeline?.name || "";
    case "valor_negocio": {
      const v = negocio?.valor_negocio || negocio?.valor_tcv || 0;
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
    }
    default: return "";
  }
}

/* ── Toolbar Button ── */
function TB({ onClick, active, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${active ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60 hover:text-slate-700"}`}>
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate-300/50 mx-0.5" />;
}

/* ── Color Picker Dropdown ── */
function ColorPicker({ editor }) {
  const [open, setOpen] = useState(false);
  const colors = ["#000000", "#374151", "#dc2626", "#ea580c", "#ca8a04", "#16a34a", "#2563eb", "#7c3aed", "#db2777"];
  return (
    <div className="relative">
      <TB onClick={() => setOpen(!open)} title="Cor do texto"><Palette className="w-3.5 h-3.5" /></TB>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-50 flex gap-1 flex-wrap w-[120px]">
          {colors.map(c => (
            <button key={c} type="button" className="w-5 h-5 rounded-full border border-slate-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              onClick={() => { editor.chain().focus().setColor(c).run(); setOpen(false); }} />
          ))}
          <button type="button" className="w-5 h-5 rounded-full border border-slate-200 bg-white flex items-center justify-center text-[8px] text-slate-400 hover:scale-110"
            onClick={() => { editor.chain().focus().unsetColor().run(); setOpen(false); }}>✕</button>
        </div>
      )}
    </div>
  );
}

/* ── Small Outline Button (same style as "Escrever email") ── */
function SmallBtn({ onClick, children, active }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-1.5 py-0.5 transition-colors ${active ? "border-slate-400 bg-slate-100 text-slate-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

/* ── Variable Dropdown ── */
function VarDropdown({ onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <SmallBtn onClick={() => setOpen(!open)} active={open}>
        <Variable className="w-3 h-3" />Variáveis
      </SmallBtn>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
          {VARIABLES.map(v => (
            <button key={v.key} type="button"
              className="block w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 text-slate-700 transition-colors"
              onClick={() => { onSelect(v.key); setOpen(false); }}>
              <span className="text-slate-400 mr-1">{`{{`}</span>{v.label}<span className="text-slate-400 ml-1">{`}}`}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Contacts Dropdown ── */
function ContactsDropdown({ contatos, empresas, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  // Build contact cards with their emails
  const cards = [];
  if (contatos?.length) {
    for (const c of contatos) {
      const emails = (c.tipos_contato || []).filter(tc => ["e-mail", "email"].includes(tc.tipo?.toLowerCase())).map(tc => tc.valor);
      if (c.custom_fields?.email && !emails.includes(c.custom_fields.email)) emails.push(c.custom_fields.email);
      cards.push({ name: c.nome, emails, type: "contato" });
    }
  }
  if (empresas?.length) {
    for (const emp of empresas) {
      cards.push({ name: emp.nome, emails: emp.email ? [emp.email] : [], type: "empresa" });
    }
  }

  if (!cards.length) return null;

  return (
    <div className="relative" ref={ref}>
      <SmallBtn onClick={() => setOpen(!open)} active={open}>
        <Plus className="w-3 h-3" />Contatos
      </SmallBtn>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[260px] max-h-[200px] overflow-y-auto">
          {cards.map((card, i) => (
            card.emails.length > 0 ? (
              card.emails.map((email, j) => (
                <button key={`${i}-${j}`} type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                  onClick={() => { onSelect(email); setOpen(false); }}>
                  <span className="text-xs font-medium text-slate-800">{card.name}</span>
                  <span className="block text-[11px] text-slate-400">{email}</span>
                </button>
              ))
            ) : (
              <div key={i} className="px-3 py-2 opacity-50">
                <span className="text-xs font-medium text-slate-600">{card.name}</span>
                <span className="block text-[10px] text-slate-400 italic">Sem e-mail cadastrado</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Editor Toolbar ── */
function EditorToolbar({ editor }) {
  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("URL da imagem:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-slate-200 bg-slate-50">
      <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrito"><Bold className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Itálico"><Italic className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Sublinhado"><UnderlineIcon className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado"><Strikethrough className="w-3.5 h-3.5" /></TB>
      <ColorPicker editor={editor} />
      <Sep />
      <TB onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Título 1"><Heading1 className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título 2"><Heading2 className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Título 3"><Heading3 className="w-3.5 h-3.5" /></TB>
      <Sep />
      <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista"><List className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada"><ListOrdered className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citação"><Quote className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Código"><Code className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linha horizontal"><Minus className="w-3.5 h-3.5" /></TB>
      <Sep />
      <TB onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Esquerda"><AlignLeft className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centralizar"><AlignCenter className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Direita"><AlignRight className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justificar"><AlignJustify className="w-3.5 h-3.5" /></TB>
      <Sep />
      <TB onClick={addLink} active={editor.isActive("link")} title="Link"><Link2 className="w-3.5 h-3.5" /></TB>
      <TB onClick={addImage} title="Imagem"><ImageIcon className="w-3.5 h-3.5" /></TB>
      <Sep />
      <TB onClick={() => editor.chain().focus().undo().run()} title="Desfazer"><Undo2 className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().redo().run()} title="Refazer"><Redo2 className="w-3.5 h-3.5" /></TB>
      <TB onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Limpar formatação"><RotateCcw className="w-3.5 h-3.5" /></TB>
    </div>
  );
}

/* ── Main Component ── */
export default function EmailComposerModal({ open, onClose, negocio, contatos = [], empresas = [], stages = [], pipeline, onSent }) {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#F43F5E";

  const [para, setPara] = useState("");
  const [cc, setCc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [assunto, setAssunto] = useState("");
  const [sending, setSending] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const assuntoRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-blue-600 underline" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: t("emails.bodyPlaceholder") }),
      TextStyle,
      Color,
      Image,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[220px] max-h-[340px] overflow-y-auto px-4 py-3 text-sm text-slate-800",
      },
    },
  });

  useEffect(() => {
    if (open) {
      setPara("");
      setCc("");
      setShowCc(false);
      setAssunto("");
      setScheduleMode(false);
      setScheduleDate("");
      setSending(false);
      if (editor) editor.commands.setContent("");
    }
  }, [open, editor]);

  const insertVarInSubject = (key) => {
    const resolved = resolveVariable(key, negocio, contatos, empresas, stages, pipeline);
    const tag = resolved || `{{${key}}}`;
    setAssunto(prev => {
      const input = assuntoRef.current;
      if (input) {
        const start = input.selectionStart || prev.length;
        return prev.slice(0, start) + tag + prev.slice(input.selectionEnd || start);
      }
      return prev + tag;
    });
  };

  const insertVarInBody = (key) => {
    if (!editor) return;
    const resolved = resolveVariable(key, negocio, contatos, empresas, stages, pipeline);
    const tag = resolved || `{{${key}}}`;
    editor.chain().focus().insertContent(tag).run();
  };

  const handleSend = async () => {
    if (!para.trim()) return toast.error(t("emails.selectRecipient"));
    if (!assunto.trim()) return toast.error(t("emails.subject") + " obrigatório");
    const html = editor?.getHTML() || "";
    if (!html || html === "<p></p>") return toast.error(t("emails.body") + " obrigatório");

    setSending(true);
    try {
      const payload = {
        negocio_id: negocio.id,
        para: para.split(",").map(e => e.trim()).filter(Boolean),
        cc: cc ? cc.split(",").map(e => e.trim()).filter(Boolean) : [],
        assunto,
        corpo_html: html,
      };
      if (scheduleMode && scheduleDate) {
        payload.agendado_para = new Date(scheduleDate).toISOString();
      }
      await axios.post(`${API}/api/emails/send`, payload, { withCredentials: true });
      toast.success(scheduleMode ? t("emails.emailScheduled") : t("emails.emailSent"));
      if (onSent) onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("emails.emailError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Send className="w-5 h-5" style={{ color: tc500 }} />{t("emails.compose")}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-3">

          {/* TO */}
          <div>
            <div className="flex items-center gap-1.5">
              <Label className="text-slate-700 text-sm">{t("emails.to")} <span className="text-rose-500">*</span></Label>
              <span className="flex-1" />
              <ContactsDropdown contatos={contatos} empresas={empresas} onSelect={email => setPara(prev => prev ? `${prev}, ${email}` : email)} />
              {!showCc && (
                <SmallBtn onClick={() => setShowCc(true)}>
                  <Plus className="w-3 h-3" />CC
                </SmallBtn>
              )}
            </div>
            <Input value={para} onChange={e => setPara(e.target.value)} placeholder={t("emails.selectRecipient")} className="mt-1" />
          </div>

          {/* CC */}
          {showCc && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 text-sm">{t("emails.cc")}</Label>
                <button type="button" onClick={() => { setShowCc(false); setCc(""); }} className="text-[10px] text-slate-400 hover:text-slate-600">
                  {t("emails.removeCc")}
                </button>
              </div>
              <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="email@example.com" className="mt-1" />
            </div>
          )}

          {/* SUBJECT */}
          <div>
            <div className="flex items-center gap-1.5">
              <Label className="text-slate-700 text-sm">{t("emails.subject")} <span className="text-rose-500">*</span></Label>
              <span className="flex-1" />
              <VarDropdown onSelect={insertVarInSubject} />
            </div>
            <Input ref={assuntoRef} value={assunto} onChange={e => setAssunto(e.target.value)} placeholder={t("emails.subjectPlaceholder")} className="mt-1" />
          </div>

          {/* RICH TEXT BODY */}
          <div>
            <div className="flex items-center gap-1.5">
              <Label className="text-slate-700 text-sm">{t("emails.body")} <span className="text-rose-500">*</span></Label>
              <span className="flex-1" />
              <VarDropdown onSelect={insertVarInBody} />
            </div>
            <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden bg-white">
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* SCHEDULE */}
          {scheduleMode && (
            <div>
              <Label className="text-slate-700 text-sm">{t("emails.scheduleDate")}</Label>
              <Input type="datetime-local" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} className="mt-1" />
            </div>
          )}

          {/* ACTIONS */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-1" />{t("common.cancel")}
            </Button>

            <button type="button"
              className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-2.5 py-1.5 transition-colors ${scheduleMode ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setScheduleMode(!scheduleMode)}>
              <Clock className="w-3.5 h-3.5" />
              {scheduleMode ? t("emails.send") : t("emails.schedule")}
            </button>

            <div className="flex-1" />

            <Button type="button" className="text-white hover:opacity-90" style={{ backgroundColor: tc500 }}
              disabled={sending} onClick={handleSend}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {scheduleMode ? t("emails.schedule") : t("emails.send")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
